import { React, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Preloader from '../components/Preloader';
import Footer from '../components/Footer';
import {
    FaEye,
    FaBullseye,
    FaLaptop,
    FaGlobeAfrica,
    FaHeart,
    FaQuoteLeft,
    FaUsers,
    FaHandHoldingUsd,
    FaLightbulb,
    FaCogs,
    FaChartLine,
    FaSeedling,
    FaUniversity,
    FaShieldAlt
} from 'react-icons/fa';
import ScrollReveal from '../components/ScrollReveal';
import Header from '../components/Header';

const teamMembers = [
    {
        name: 'fullname',
        role: 'CEO',
        image: '../assets/mainlogo.jpg',
        bio: 'Leading with a vision to empower individuals and businesses through accessible financial solutions.'
    },
    // {
    //     name: 'fullname',
    //     role: 'Head of Operations',
    //     image: '../assets/avatar.jpg',
    //     bio: 'Ensuring smooth, effective and efficient loan processing to meet our clients\' urgent business needs.'
    // },
    // {
    //     name: 'fullname',
    //     role: 'Credit Manager',
    //     image: '../assets/avatar.jpg',
    //     bio: 'Expert in risk assessment and financial planning, dedicated to helping you manage your credit responsibly.'
    // },
];

const features = [
    {
        icon: <FaBullseye className="text-3xl" />,
        title: 'Tailored Solutions',
        description: 'We offer credit facilities customized to meet the unique needs of salary workers and SMEs.'
    },
    {
        icon: <FaGlobeAfrica className="text-3xl" />,
        title: 'Nationwide Reach',
        description: 'Serving clients across Ghana with accessible financial products and services.'
    },
    {
        icon: <FaLaptop className="text-3xl" />,
        title: 'Digital Convenience',
        description: 'Apply for loans and manage your account easily through our digital platforms.'
    },
    {
        icon: <FaHeart className="text-3xl" />,
        title: 'Customer First',
        description: 'Your financial well-being is our priority. We treat every client with respect and integrity.'
    }
];

const strategicPillars = [
    {
        title: 'Customer Excellence',
        description: 'We put our customers at the heart of everything we do, delivering exceptional value and building lasting relationships.',
        icon: FaUsers
    },
    {
        title: 'Responsible Lending',
        description: 'We promote financial inclusion by offering fair, transparent and sustainable lending solutions that strengthen lives and businesses.',
        icon: FaHandHoldingUsd
    },
    {
        title: 'Digital Innovation',
        description: 'We leverage technology and innovation to improve access, experience and efficiency in the services we provide.',
        icon: FaLightbulb
    },
    {
        title: 'Operational Excellence',
        description: 'We are committed to efficient processes, strong controls and continuous improvement to deliver reliable and dependable services.',
        icon: FaCogs
    },
    {
        title: 'People Development',
        description: 'We invest in our people, empower growth and foster a culture of learning, collaboration and high performance.',
        icon: FaChartLine
    },
    {
        title: 'Sustainable Growth',
        description: 'We drive long-term value by supporting thriving communities, responsible practices and growth that creates a positive and enduring impact.',
        icon: FaSeedling
    },
    {
        title: 'Governance & Compliance',
        description: 'We uphold the highest standards of integrity, transparency and compliance in all that we do.',
        icon: FaUniversity
    }
];

const testimonials = [
    {
        quote: "Querencia Micro Credit provided the quick and easy loan I needed to expand my shop. Their process is straightforward and the staff are incredibly supportive.",
        author: "Adoma A.",
        role: "Retail Shop Owner, Accra"
    },
    {
        quote: "As a salaried worker, getting a quick advance was seamless. The digital platform is user-friendly and the funds were disbursed in less than 24 hours. Highly recommended!",
        author: "Samuel B.",
        role: "IT Professional, Kumasi"
    },
    {
        quote: "The SME loan from Querencia was a game-changer for my catering business. They understood my needs and provided a flexible repayment plan that worked for me.",
        author: "Esi T.",
        role: "Catering Business Owner, Takoradi"
    }
];

const AboutUs = () => {

    const sliderRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    const handleMouseDown = (e) => {
        if (!sliderRef.current) return;
        setIsDragging(true);
        setStartX(e.pageX - sliderRef.current.offsetLeft);
        setScrollLeft(sliderRef.current.scrollLeft);
        sliderRef.current.style.cursor = 'grabbing';
    };

    const handleMouseLeave = () => {
        if (!sliderRef.current) return;
        setIsDragging(false);
        sliderRef.current.style.cursor = 'grab';
    };

    const handleMouseUp = () => {
        if (!sliderRef.current) return;
        setIsDragging(false);
        sliderRef.current.style.cursor = 'grab';
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !sliderRef.current) return;
        e.preventDefault();
        const x = e.pageX - sliderRef.current.offsetLeft;
        const walk = (x - startX) * 2; 
        sliderRef.current.scrollLeft = scrollLeft - walk;
    };

    return (
        <div className='font-nixie-one bg-white min-h-screen text-gray-800'>
            <Helmet>
                <title>About Us - Querencia Micro Credit</title>
                <meta name="description" content="Learn about Querencia Micro Credit, a licensed financial institution in Ghana dedicated to empowering individuals and SMEs with flexible credit solutions." />
                <meta name="keywords" content="sme loans, business loans, micro credit, Ghana, small business, querencia, finance, loans, africa loans, fast cash, money, quick loans, Accra." />
                <meta property="og:title" content="About Us - Querencia Micro Credit" />
                <meta property="og:description" content="Learn about Querencia Micro Credit, a licensed financial institution in Ghana dedicated to empowering individuals and SMEs with flexible credit solutions." />
            </Helmet>
            <Header />
            <Preloader />
            <main>
                {/* Hero Section */}
                <section className="relative h-[40vh] md:h-[50vh] bg-cover bg-center" style={{ backgroundImage: "url('/assets/aboutus.jpg')" }}>
                    <div className="absolute inset-0 bg-blue-900/60 flex items-center justify-center">
                        <ScrollReveal animationClass="animate-fade-up" threshold={0.1} delay={200}>
                        <div className="text-center text-white px-4">
                            <h1 className="text-2xl md:text-3xl font-bold mb-4 capitalize tracking-wide">about querencia micro credit</h1>
                            <p className="text-sm md:text-sm max-w-2xl mx-auto font-light">Learn more about our micro credit company, mission, and values.</p>
                        </div>
                        </ScrollReveal>
                    </div>
                </section>

                {/* Our Story Section */}
                <section className="py-20 px-4 md:px-10 lg:px-44 mx-auto bg-white">
                    <div className="container mx-auto grid lg:grid-cols-2 gap-12 items-center">
                        <ScrollReveal animationClass="animate-slide-left" threshold={0.1}>
                            <div className="relative h-80 lg:h-full rounded-2xl overflow-hidden shadow-lg">
                                <img src="/assets/mainlogo.jpg" alt="Our Team discussing financial solutions" className="w-80 h-80 object-cover" loading='lazy'/>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                            </div>
                        </ScrollReveal>
                        <ScrollReveal animationClass="animate-fade-up" threshold={0.1} delay={200}>
                            <div className="text-left">
                                <h2 className="text-2xl text-center font-semibold text-gray-800  mb-6 capitalize">About querencia micro credit</h2>
                                <p className="text-gray-600 leading-relaxed text-sm text-justify font-semibold mb-4">
                                    Querencia Micro Credit was established with a clear purpose: to bridge the financial gap for individuals and Small to Medium-sized Enterprises (SMEs) in Ghana. We understand that access to timely credit is a catalyst for growth and stability.
                                </p>
                                <p className="text-gray-600 leading-relaxed text-justify text-sm font-semibold mb-4">
                                    As a licensed financial institution, we are dedicated to providing flexible, transparent, and customer-centric financial solutions that empower our clients to achieve their personal and business goals.
                                </p>
                            </div>
                        </ScrollReveal>
                    </div>
                </section>

                {/* Mission & Vision Section */}
                <section className="bg-gray-50 py-20 px-4 md:px-10 lg:px-44 mx-auto">
                    <div className="container mx-auto">
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            <ScrollReveal animationClass="animate-fade-up" threshold={0.1} delay={100}>
                                <div className="bg-white p-8 rounded-xl shadow-md h-full">
                                    <FaBullseye className="text-4xl text-blue-600 mb-4" />
                                    <h3 className="text-xl font-semibold mb-3">Our Mission</h3>
                                    <p className="text-gray-600 text-sm leading-relaxed font-semibold text-justify">We provide fast, flexible and  customer-centred financial services that empowers individuals, salaried workers, entrepreneurs and SMEs to achieve sustainable growth while creating lasting value for our customers, employees and communities.</p>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animationClass="animate-fade-up" threshold={0.1} delay={200}>
                                <div className="bg-white p-8 rounded-xl shadow-md h-full">
                                    <FaEye className="text-4xl text-blue-600 mb-4" />
                                    <h3 className="text-xl font-semibold mb-3">Our Vision</h3>
                                    <p className="text-gray-600 text-sm leading-relaxed font-semibold text-justify">To be Ghana's most trusted and impactful microcredit institution, empowering individuals, businesses and communities through accessible, innovative and responsible financial solutions.</p>
                                </div>
                            </ScrollReveal>
                            <ScrollReveal animationClass="animate-fade-up" threshold={0.1} delay={300}>
                                <div className="bg-white p-8 rounded-xl shadow-md h-full md:col-span-2 lg:col-span-1">
                                    <FaHeart className="text-4xl text-blue-600 mb-4" />
                                    <h3 className="text-xl font-semibold mb-3">Our Core Values</h3>
                                    <div className='flex flex-col gap-2 items-start text-justify'>
                                        <p className="text-gray-600 leading-relaxed capitalize font-semibold text-sm">Responsiveness</p>
                                        <p className="text-gray-600 leading-relaxed capitalize font-semibold text-sm">Integrity</p>
                                        <p className="text-gray-600 leading-relaxed capitalize font-semibold text-sm">Transparent</p>
                                        <p className="text-gray-600 leading-relaxed capitalize font-semibold text-sm">Excellence</p>
                                    </div>
                                </div>
                            </ScrollReveal>
                        </div>
                    </div>
                </section>

                {/* Strategic Pillars Section */}
                <section id="strategic-pillars" aria-labelledby="strategic-pillars-heading" className="relative scroll-mt-20 overflow-hidden bg-slate-50 py-20 px-4 md:px-10 lg:px-16">
                    <div aria-hidden="true" className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-blue-700/10 blur-3xl"></div>
                    <div aria-hidden="true" className="absolute -right-24 bottom-16 h-72 w-72 rounded-full bg-orange-500/10 blur-3xl"></div>

                    <div className="relative mx-auto max-w-6xl">
                        <ScrollReveal animationClass="animate-fade-up" threshold={0.1} delay={100}>
                            <div className="mx-auto mb-12 max-w-3xl text-center">
                                <span className="mb-4 inline-flex items-center rounded-full border border-blue-100 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-800 shadow-sm">
                                    What guides us
                                </span>
                                <h2 id="strategic-pillars-heading" className="text-3xl font-bold tracking-tight text-[#073f8c] sm:text-4xl lg:text-5xl">
                                    Our Strategic Pillars
                                </h2>
                                <div className="mx-auto my-5 flex w-28 items-center gap-2">
                                    <span className="h-px flex-1 bg-orange-500"></span>
                                    <span className="h-2.5 w-2.5 rotate-45 bg-orange-500"></span>
                                    <span className="h-px flex-1 bg-orange-500"></span>
                                </div>
                                <p className="font-cormorant-upright text-xl font-semibold italic text-orange-600 sm:text-2xl">
                                    Building a stronger future, together.
                                </p>
                            </div>
                        </ScrollReveal>

                        <ol className="space-y-4">
                            {strategicPillars.map((pillar, index) => {
                                const Icon = pillar.icon;
                                const isOrange = index % 2 !== 0;

                                return (
                                    <li key={pillar.title}>
                                        <ScrollReveal animationClass="animate-fade-up" threshold={0.08} delay={100 + ((index % 3) * 75)}>
                                            <article className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_12px_35px_-24px_rgba(15,23,42,0.55)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_20px_45px_-25px_rgba(15,23,42,0.45)] sm:p-6">
                                                <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-1.5 ${isOrange ? 'bg-orange-500' : 'bg-[#073f8c]'}`}></span>
                                                <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-6">
                                                    <div className={`relative ml-1 flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl text-white shadow-lg transition-transform duration-300 group-hover:scale-105 sm:h-[4.5rem] sm:w-[4.5rem] ${isOrange ? 'bg-gradient-to-br from-orange-400 to-orange-600 shadow-orange-200' : 'bg-gradient-to-br from-blue-700 to-[#052f6b] shadow-blue-200'}`}>
                                                        <Icon aria-hidden="true" className="text-3xl" />
                                                        <span className={`absolute -bottom-2.5 -right-2.5 flex h-8 w-8 items-center justify-center rounded-full border-4 border-white text-xs font-extrabold text-white ${isOrange ? 'bg-orange-500' : 'bg-[#073f8c]'}`}>
                                                            {index + 1}
                                                        </span>
                                                    </div>

                                                    <span aria-hidden="true" className="hidden h-14 w-px bg-slate-200 sm:block"></span>

                                                    <div className="min-w-0 flex-1">
                                                        <h3 className={`mb-1.5 text-lg font-extrabold uppercase tracking-wide sm:text-xl ${isOrange ? 'text-orange-600' : 'text-[#073f8c]'}`}>
                                                            {pillar.title}
                                                        </h3>
                                                        <p className="text-sm font-semibold leading-6 text-slate-600 sm:text-[0.95rem]">
                                                            {pillar.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            </article>
                                        </ScrollReveal>
                                    </li>
                                );
                            })}
                        </ol>

                        <ScrollReveal animationClass="animate-fade-up" threshold={0.1} delay={200}>
                            <div className="mt-8 flex flex-col items-center gap-4 rounded-2xl bg-gradient-to-r from-[#052f6b] via-[#073f8c] to-blue-700 px-6 py-6 text-center text-white shadow-xl shadow-blue-900/15 sm:flex-row sm:px-8 sm:text-left">
                                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/20">
                                    <FaShieldAlt aria-hidden="true" className="text-2xl text-orange-400" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-blue-100">Guided by our pillars. Driven by our purpose.</p>
                                    <p className="mt-1 font-cormorant-upright text-xl font-bold italic text-orange-300 sm:text-2xl">
                                        Empowering dreams through responsible finance.
                                    </p>
                                </div>
                            </div>
                        </ScrollReveal>
                    </div>
                </section>

                {/* Why Choose Us Section */}
                <section className="py-20 px-4 md:px-10 lg:px-44 mx-auto">
                    <div className="container mx-auto text-center">
                        <ScrollReveal animationClass="animate-fade-up" threshold={0.1} delay={200}>
                            <h2 className="text-2xl font-semibold text-gray-800 mb-12">Why Choose Querencia?</h2>
                        </ScrollReveal>
                        <ScrollReveal animationClass="animate-fade-in" threshold={0.1} delay={400}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                                {features.map((feature, index) => (
                                    <div key={index} className="group bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 transition-all duration-300 hover:-translate-y-1">
                                        <div className="w-16 h-16 mx-auto bg-blue-50 rounded-full flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors duration-300">
                                            <span className="text-blue-600 group-hover:text-white transition-colors duration-300">
                                                {feature.icon}
                                            </span>
                                        </div>
                                        <h4 className="text-lg font-bold mb-3 text-gray-900">{feature.title}</h4>
                                        <p className="text-gray-600 text-sm leading-relaxed font-semibold">{feature.description}</p>
                                    </div>
                                ))}
                            </div>
                        </ScrollReveal>
                    </div>
                </section>

                {/* Meet the Team Section */}
                {/* <section className="bg-gray-50 py-20 px-4">
                    <div className="container mx-auto text-center">
                        <ScrollReveal animationClass="animate-fade-up" threshold={0.1}>
                            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Meet Our Management</h2>
                            <p className="text-gray-600 text-sm max-w-2xl font-semibold mx-auto mb-12">The dedicated professionals behind our success.</p>
                        </ScrollReveal>
                        <ScrollReveal animationClass="animate-fade-in" threshold={0.1} delay={200}>
                            <div
                                ref={sliderRef}
                                onMouseDown={handleMouseDown}
                                onMouseLeave={handleMouseLeave}
                                onMouseUp={handleMouseUp}
                                onMouseMove={handleMouseMove}
                                className="flex overflow-x-auto scroll-smooth justify-center items-center scrollbar-hide space-x-8 pb-4 cursor-grab"
                            >
                                {teamMembers.map((member, index) => (
                                    <div key={index} className="flex-shrink-0 w-72 bg-white rounded-xl shadow-lg p-6 text-center transform transition-transform duration-300 hover:scale-105">
                                        <img src={member.image} alt={member.name} className="w-32 h-32 rounded-full object-cover mx-auto mb-4 border-4 border-white shadow-md" loading='lazy'/>
                                        <h4 className="text-xl font-bold text-blue-900">{member.name}</h4>
                                        <p className="text-blue-600 font-semibold mb-3">{member.role}</p>
                                        <p className="text-gray-600 text-sm leading-relaxed font-semibold">{member.bio}</p>
                                    </div>
                                ))}
                            </div>
                        </ScrollReveal>
                    </div>
                </section> */}

                {/* Testimonials Section */}
                <section className="py-20 px-4 md:px-10 lg:px-44 mx-auto bg-white overflow-hidden">
                    <div className="container mx-auto text-center">
                        <ScrollReveal animationClass="animate-fade-up" threshold={0.1}>
                            <h2 className="text-2xl font-semibold text-gray-800 mb-4">What Our Clients Say</h2>
                            <p className="text-gray-600 text-sm font-semibold max-w-2xl mx-auto mb-12">Real stories from people we've helped.</p>
                        </ScrollReveal>
                        <div className="relative w-full overflow-hidden">
                            <div className="flex w-max animate-slide hover:[animation-play-state:paused]">
                                {[...testimonials, ...testimonials, ...testimonials, ...testimonials].map((testimonial, index) => (
                                    <div key={index} className="w-[300px] md:w-[400px] mx-4 flex-shrink-0">
                                        <div className="bg-gray-50 p-8 rounded-xl shadow-lg h-full flex flex-col border-t-4 border-blue-500 text-left">
                                        <FaQuoteLeft className="text-3xl text-blue-300 mb-4" />
                                        <p className="text-gray-600 italic font-semibold text-sm flex-grow">"{testimonial.quote}"</p>
                                        <div className="mt-6 pt-4 border-t border-gray-200">
                                            <p className="font-bold text-blue-900">{testimonial.author}</p>
                                            <p className="text-xs font-semibold text-gray-500">{testimonial.role}</p>
                                        </div>
                                    </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    )
}

export default AboutUs;
